import React, { useState, useEffect, useCallback } from 'react';
import {
    RefreshCw, TrendingUp, DollarSign, Target, Calendar, PieChart,
    Eye, MousePointer, BarChart3, AlertTriangle, Zap, Award
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import {
    formatNumber, formatCurrency,
    DATE_RANGE_PRESETS, DATE_RANGE_LABELS, getDateRange
} from '../utils/helpers';
import {
    fetchPlacementStats,
    getCachedPlacementStats,
    setCachedPlacementStats
} from '../services/campaignCreativesService';
import { CATEGORY_ICONS } from '../constants/placements';

const PLATFORMS = [
    { id: 'meta', label: 'Meta', icon: '📘' },
    { id: 'google', label: 'Google', icon: '🔍' },
    { id: 'tiktok', label: 'TikTok', icon: '🎵' },
    { id: 'amazon', label: 'Amazon', icon: '📦' }
];

const PlacementInsights = () => {
    const { selectedBrand } = useApp();
    const { currentUser } = useAuth();

    // State
    const [stats, setStats] = useState(null);
    const [platformConnections, setPlatformConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlatform, setSelectedPlatform] = useState('meta');
    const [selectedDateRange, setSelectedDateRange] = useState(DATE_RANGE_PRESETS.LAST_30_DAYS);
    const [dateRange, setDateRange] = useState(() => getDateRange(DATE_RANGE_PRESETS.LAST_30_DAYS));

    // Fetch platform connections
    const fetchConnections = useCallback(async () => {
        if (!selectedBrand || !currentUser) return;
        try {
            const response = await fetch(
                `http://localhost:3001/api/campaigns/connections?userId=${currentUser.uid}&brandId=${selectedBrand.id}`
            );
            const data = await response.json();
            setPlatformConnections(data.connections || []);
        } catch (error) {
            console.error('Error fetching connections:', error);
        }
    }, [selectedBrand, currentUser]);

    useEffect(() => {
        fetchConnections();
    }, [fetchConnections]);

    // Fetch placement stats when platform or date range changes
    useEffect(() => {
        if (selectedBrand && currentUser) {
            loadStats();
        }
    }, [selectedBrand, currentUser, selectedPlatform, dateRange]);

    const loadStats = async () => {
        if (!selectedBrand || !currentUser) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Check cache first
        const cached = getCachedPlacementStats(selectedBrand.id, selectedPlatform, dateRange);
        if (cached) {
            setStats(cached);
            setLoading(false);
            return;
        }

        try {
            const data = await fetchPlacementStats(
                currentUser.uid,
                selectedBrand.id,
                selectedPlatform,
                dateRange
            );
            setStats(data);
            setCachedPlacementStats(selectedBrand.id, selectedPlatform, data, dateRange);
        } catch (err) {
            console.error('Error fetching placement stats:', err);
            setStats(null);
        } finally {
            setLoading(false);
        }
    };

    // Handle date range change
    const handleDateRangeChange = (preset) => {
        setSelectedDateRange(preset);
        setDateRange(getDateRange(preset));
    };

    // Check if platform is connected
    const isPlatformConnected = platformConnections.some(
        conn => conn.platform === selectedPlatform && conn.status === 'active'
    );

    if (!selectedBrand) {
        return (
            <div style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
                <p className="text-secondary">Please select a brand to view placement insights.</p>
            </div>
        );
    }

    const { placements, categories, totals, insights } = stats || {};

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 'var(--space-6)'
            }}>
                <div>
                    <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, marginBottom: 'var(--space-2)' }}>
                        Placement Insights
                    </h1>
                    <p className="text-secondary">
                        Analyze performance across ad placements and optimize budget allocation
                    </p>
                </div>

                {/* Date Range Selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <Calendar size={18} className="text-secondary" />
                    <select
                        value={selectedDateRange}
                        onChange={(e) => handleDateRangeChange(e.target.value)}
                        className="form-select"
                        style={{
                            padding: 'var(--space-2) var(--space-3)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-surface)',
                            color: 'var(--color-text-primary)',
                            fontSize: 'var(--font-size-sm)',
                            minWidth: '180px'
                        }}
                    >
                        {Object.entries(DATE_RANGE_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Platform Tabs */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-6)',
                borderBottom: '1px solid var(--color-border-light)',
                paddingBottom: 'var(--space-1)'
            }}>
                {PLATFORMS.map(platform => {
                    const isConnected = platformConnections.some(
                        c => c.platform === platform.id && c.status === 'active'
                    );
                    const isSelected = selectedPlatform === platform.id;

                    return (
                        <button
                            key={platform.id}
                            onClick={() => setSelectedPlatform(platform.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-3) var(--space-4)',
                                background: isSelected ? 'var(--color-primary)' : 'transparent',
                                color: isSelected ? 'white' : 'var(--color-text-primary)',
                                border: 'none',
                                borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
                                cursor: 'pointer',
                                fontWeight: isSelected ? 600 : 400,
                                opacity: isConnected ? 1 : 0.5,
                                transition: 'all var(--transition-fast)'
                            }}
                        >
                            <span>{platform.icon}</span>
                            <span>{platform.label}</span>
                            {!isConnected && (
                                <span style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)'
                                }}>
                                    Not connected
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Loading State */}
            {loading && (
                <div className="card" style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
                    <RefreshCw size={32} className="spin text-secondary" />
                    <p className="text-secondary" style={{ marginTop: 'var(--space-4)' }}>
                        Loading placement insights...
                    </p>
                </div>
            )}

            {/* Not Connected State */}
            {!loading && !isPlatformConnected && (
                <div className="card" style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
                    <AlertTriangle size={48} className="text-warning" style={{ opacity: 0.5 }} />
                    <h3 style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>
                        {PLATFORMS.find(p => p.id === selectedPlatform)?.label} Not Connected
                    </h3>
                    <p className="text-secondary">
                        Connect your {PLATFORMS.find(p => p.id === selectedPlatform)?.label} Ads account
                        in the Campaign Monitoring page to see placement insights.
                    </p>
                </div>
            )}

            {/* No Data State */}
            {!loading && isPlatformConnected && (!stats || !placements || placements.length === 0) && (
                <div className="card" style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
                    <BarChart3 size={48} className="text-secondary" style={{ opacity: 0.3 }} />
                    <h3 style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>
                        No Placement Data
                    </h3>
                    <p className="text-secondary">
                        No placement data available for the selected period.
                        Make sure you have active campaigns running.
                    </p>
                </div>
            )}

            {/* Main Content */}
            {!loading && isPlatformConnected && stats && placements && placements.length > 0 && (
                <>
                    {/* Key Insights Cards */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 'var(--space-4)',
                        marginBottom: 'var(--space-6)'
                    }}>
                        {/* Total Spend */}
                        <div className="card" style={{ padding: 'var(--space-4)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                <DollarSign size={18} className="text-secondary" />
                                <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Total Spend</span>
                            </div>
                            <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>
                                {formatCurrency(totals?.spend || 0)}
                            </div>
                        </div>

                        {/* Total Revenue */}
                        <div className="card" style={{ padding: 'var(--space-4)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                <TrendingUp size={18} className="text-secondary" />
                                <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Total Revenue</span>
                            </div>
                            <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>
                                {formatCurrency(totals?.revenue || 0)}
                            </div>
                        </div>

                        {/* Overall ROAS */}
                        <div className="card" style={{ padding: 'var(--space-4)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                <Target size={18} className="text-secondary" />
                                <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Overall ROAS</span>
                            </div>
                            <div style={{
                                fontSize: 'var(--font-size-2xl)',
                                fontWeight: 700,
                                color: (totals?.roas || 0) >= 2 ? 'var(--color-success)' :
                                       (totals?.roas || 0) >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                            }}>
                                {(totals?.roas || 0).toFixed(2)}x
                            </div>
                        </div>

                        {/* Placements Count */}
                        <div className="card" style={{ padding: 'var(--space-4)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                <PieChart size={18} className="text-secondary" />
                                <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>Placements</span>
                            </div>
                            <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>
                                {placements.length}
                            </div>
                        </div>
                    </div>

                    {/* Best Performers Section */}
                    {insights && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 'var(--space-4)',
                            marginBottom: 'var(--space-6)'
                        }}>
                            {insights.bestRoas && (
                                <div className="card" style={{
                                    padding: 'var(--space-4)',
                                    borderLeft: '4px solid var(--color-success)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                                        <Award size={20} style={{ color: 'var(--color-success)' }} />
                                        <span style={{ fontWeight: 600 }}>Best ROAS</span>
                                    </div>
                                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                        {insights.bestRoas.name}
                                    </div>
                                    <div style={{ color: 'var(--color-success)', fontWeight: 600, fontSize: 'var(--font-size-xl)' }}>
                                        {insights.bestRoas.roas.toFixed(2)}x ROAS
                                    </div>
                                    <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-1)' }}>
                                        {formatCurrency(insights.bestRoas.spend)} spent
                                    </div>
                                </div>
                            )}

                            {insights.mostEfficient && (
                                <div className="card" style={{
                                    padding: 'var(--space-4)',
                                    borderLeft: '4px solid var(--color-primary)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                                        <Zap size={20} style={{ color: 'var(--color-primary)' }} />
                                        <span style={{ fontWeight: 600 }}>Most Efficient</span>
                                    </div>
                                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                        {insights.mostEfficient.name}
                                    </div>
                                    <div style={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: 'var(--font-size-xl)' }}>
                                        {formatCurrency(insights.mostEfficient.cpc)} CPC
                                    </div>
                                    <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-1)' }}>
                                        {insights.mostEfficient.ctr.toFixed(2)}% CTR
                                    </div>
                                </div>
                            )}

                            {insights.highestVolume && (
                                <div className="card" style={{
                                    padding: 'var(--space-4)',
                                    borderLeft: '4px solid var(--color-warning)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                                        <Eye size={20} style={{ color: 'var(--color-warning)' }} />
                                        <span style={{ fontWeight: 600 }}>Highest Volume</span>
                                    </div>
                                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                        {insights.highestVolume.name}
                                    </div>
                                    <div style={{ color: 'var(--color-warning)', fontWeight: 600, fontSize: 'var(--font-size-xl)' }}>
                                        {formatNumber(insights.highestVolume.impressions)}
                                    </div>
                                    <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-1)' }}>
                                        impressions
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Category Performance */}
                    {categories && categories.length > 0 && (
                        <div className="card" style={{ marginBottom: 'var(--space-6)', overflow: 'hidden' }}>
                            <div style={{
                                padding: 'var(--space-4)',
                                borderBottom: '1px solid var(--color-border-light)',
                                background: 'var(--color-bg-secondary)'
                            }}>
                                <h3 style={{ margin: 0 }}>Performance by Category</h3>
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: 'var(--space-4)',
                                padding: 'var(--space-4)'
                            }}>
                                {categories.map(cat => (
                                    <div
                                        key={cat.category}
                                        style={{
                                            padding: 'var(--space-4)',
                                            background: 'var(--color-bg-secondary)',
                                            borderRadius: 'var(--radius-lg)',
                                            border: '1px solid var(--color-border-light)'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                                            <span style={{ fontSize: 'var(--font-size-xl)' }}>{CATEGORY_ICONS[cat.category] || '📍'}</span>
                                            <span style={{ fontWeight: 600, fontSize: 'var(--font-size-lg)' }}>{cat.category}</span>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                                            <div>
                                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Spend</div>
                                                <div style={{ fontWeight: 600 }}>{formatCurrency(cat.spend)}</div>
                                            </div>
                                            <div>
                                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>ROAS</div>
                                                <div style={{
                                                    fontWeight: 600,
                                                    color: cat.roas >= 2 ? 'var(--color-success)' :
                                                           cat.roas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                                                }}>
                                                    {cat.roas.toFixed(2)}x
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>CTR</div>
                                                <div style={{ fontWeight: 600 }}>{cat.ctr.toFixed(2)}%</div>
                                            </div>
                                            <div>
                                                <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Placements</div>
                                                <div style={{ fontWeight: 600 }}>{cat.placements?.length || 0}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Detailed Placement Table */}
                    <div className="card" style={{ overflow: 'hidden' }}>
                        <div style={{
                            padding: 'var(--space-4)',
                            borderBottom: '1px solid var(--color-border-light)',
                            background: 'var(--color-bg-secondary)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0 }}>All Placements</h3>
                            <button
                                onClick={loadStats}
                                className="btn btn-ghost"
                                style={{ padding: 'var(--space-2)' }}
                            >
                                <RefreshCw size={16} />
                            </button>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--color-border-light)', background: 'var(--color-bg-tertiary)' }}>
                                        <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Placement</th>
                                        <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Category</th>
                                        <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Spend</th>
                                        <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Revenue</th>
                                        <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: 600, color: 'var(--color-text-secondary)' }}>ROAS</th>
                                        <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: 600, color: 'var(--color-text-secondary)' }}>CTR</th>
                                        <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: 600, color: 'var(--color-text-secondary)' }}>CPC</th>
                                        <th style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Impressions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {placements.map((p, index) => (
                                        <tr
                                            key={p.placementId}
                                            style={{
                                                borderBottom: '1px solid var(--color-border-light)',
                                                background: index === 0 ? 'var(--color-success-bg)' : 'transparent'
                                            }}
                                        >
                                            <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                    <span>{p.icon}</span>
                                                    <span style={{ fontWeight: 500 }}>{p.name}</span>
                                                    {index === 0 && (
                                                        <span style={{
                                                            background: 'var(--color-success)',
                                                            color: 'white',
                                                            fontSize: 'var(--font-size-xs)',
                                                            padding: '2px 8px',
                                                            borderRadius: 'var(--radius-full)',
                                                            fontWeight: 600
                                                        }}>
                                                            Top Spend
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-1)',
                                                    padding: 'var(--space-1) var(--space-2)',
                                                    background: 'var(--color-bg-tertiary)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}>
                                                    {CATEGORY_ICONS[p.category] || '📍'} {p.category}
                                                </span>
                                            </td>
                                            <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: 500 }}>
                                                {formatCurrency(p.metrics.spend)}
                                            </td>
                                            <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right', fontWeight: 500 }}>
                                                {formatCurrency(p.metrics.revenue)}
                                            </td>
                                            <td style={{
                                                padding: 'var(--space-3) var(--space-4)',
                                                textAlign: 'right',
                                                fontWeight: 600,
                                                color: p.metrics.roas >= 2 ? 'var(--color-success)' :
                                                       p.metrics.roas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                                            }}>
                                                {p.metrics.roas.toFixed(2)}x
                                            </td>
                                            <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right' }}>
                                                {p.metrics.ctr.toFixed(2)}%
                                            </td>
                                            <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right' }}>
                                                {formatCurrency(p.metrics.cpc)}
                                            </td>
                                            <td style={{ padding: 'var(--space-3) var(--space-4)', textAlign: 'right' }}>
                                                {formatNumber(p.metrics.impressions)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PlacementInsights;
